import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Award } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const values = [
    { icon: Zap, title: "Speed Without Shortcuts", desc: "We close in 24 hours because our underwriting is built for it — not because we skip diligence." },
    { icon: ShieldCheck, title: "Integrity Over Volume", desc: "We close what we approve. No bait-and-switch. No surprise terms. ISOs and merchants both know what they're signing." },
    { icon: Award, title: "Built for the Hard Files", desc: "C/D paper, 4th-8th positions, complex revenue patterns — the deals other funders pass on are the deals we build relationships on." },
];

export default function About() {
    usePageTitle("About", "Lended is a New York-based direct funder serving experienced ISO partners and small businesses across the United States since 2018.");
    return (
        <>
            <section className="bg-white relative overflow-hidden" data-testid="about-hero">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="container-lended relative pt-20 pb-16 md:pt-28 md:pb-20 max-w-4xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="divider-orange" />
                        <span className="eyebrow">About Lended</span>
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                        We built the funder we wished existed when <span className="text-[#FF6B1A]">we were ISOs.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-black/70 max-w-3xl leading-relaxed">
                        Lended is a New York-based direct funder serving experienced ISO partners and small
                        businesses across the United States. Founded in 2018 by veterans of the alternative-finance
                        industry, we've deployed over <strong className="text-black">$420 million</strong> in working
                        capital to merchants who power the American economy — restaurants, contractors, healthcare
                        practices, retailers, and the trades.
                    </p>
                </div>
            </section>

            <section className="bg-[#F8F5F2] py-20" data-testid="about-mission">
                <div className="container-lended grid md:grid-cols-2 gap-16 items-start">
                    <div>
                        <span className="eyebrow">Our Mission</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-[1]">
                            Real capital. <br /> Real people. <br /> Real closings.
                        </h2>
                    </div>
                    <div className="text-black/75 text-lg space-y-5 leading-relaxed">
                        <p>
                            Most funders treat ISOs like ticket numbers and merchants like spreadsheets. We don't. Every
                            deal at Lended is reviewed by a human underwriter with authority to approve, structure, and
                            close — not just kick a file back to the queue.
                        </p>
                        <p>
                            We focus on the deals other funders walk away from: 2nd-8th positions, $250K to $3M, C and D
                            paper with healthy revenue. We win on creativity, speed, and trust — and we measure ourselves
                            on the only number that matters: deals closed.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-white py-20 md:py-28" data-testid="about-values">
                <div className="container-lended">
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl leading-[0.98]">
                        Three things we never compromise on.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-3 gap-px bg-black/10">
                        {values.map((v) => (
                            <div key={v.title} className="bg-white p-10" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                <v.icon className="text-[#FF6B1A]" size={36} />
                                <div className="font-display font-bold text-2xl mt-5">{v.title}</div>
                                <p className="text-black/65 mt-3 leading-relaxed">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#0A0A0A] text-white py-20" data-testid="about-cta">
                <div className="container-lended flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div>
                        <h3 className="font-display text-3xl md:text-4xl font-bold leading-[1] max-w-xl">
                            Ready to fund the deal nobody else will touch?
                        </h3>
                    </div>
                    <Link to="/#apply" className="btn-orange" data-testid="about-final-cta">
                        Submit a Deal <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </>
    );
}
